import React, { useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./FAQSection.css";

const SECURITY_FAQ_ANSWER =
  "Highwatch operates with SOC 2–aligned controls (Type I at launch; Type II on roadmap), HIPAA readiness, and full encryption (AES-256 at rest, TLS 1.3 in transit). All customer data is isolated, auditable, and never used for model training without explicit consent.";

const PRICING_FAQ_ITEMS = [
  {
    question: "How does Highwatch ensure data security and compliance?",
    answer: SECURITY_FAQ_ANSWER,
  },
  {
    question: "Is Highwatch compliant with global enterprise standards?",
    answer: SECURITY_FAQ_ANSWER,
  },
  {
    question: "What happens to enterprise data after contract termination?",
    answer: SECURITY_FAQ_ANSWER,
  },
];

const FAQ_NAV_ITEMS = [
  { id: "security", label: "Security & Compliance" },
  { id: "integrations", label: "Integrations" },
  { id: "getting-started", label: "Getting Started" },
  { id: "support", label: "Support" },
];

const HOME_FAQ_ANSWER =
  "Insurance is live in production. Finance and Healthcare verticals are under active development on the same core intelligence layer. The underlying platform is industry-agnostic and can be extended to adjacent regulated domains under enterprise engagements.";

const HOME_FAQS = [
  {
    question: "What industries does Highwatch support today?",
    answer: HOME_FAQ_ANSWER,
  },
  {
    question: "How does Highwatch ensure data security and compliance?",
    answer: HOME_FAQ_ANSWER,
  },
  {
    question: "Can Highwatch integrate with our existing systems?",
    answer: HOME_FAQ_ANSWER,
  },
  {
    question: "What happens to enterprise data after contract termination?",
    answer: HOME_FAQ_ANSWER,
  },
];

function HomeCta({ ctaSecondaryLink = "/ProductIntelligence" }) {
  const navigate = useNavigate();

  return (
    <section className="hw-home-cta">
      <h2>See Highwatch on your own submissions.</h2>
      <p>Book a 30-minute walkthrough with our team — no commitment, no generic demo data.</p>
      <div className="hw-home-cta__actions">
        <button type="button" className="hw-home-cta__primary" onClick={() => navigate("/Contact")}>
          Get a demo
        </button>
        <Link to={ctaSecondaryLink} className="hw-home-cta__secondary">
          Learn How It Works
        </Link>
      </div>
    </section>
  );
}

function PricingFaqSection() {
  const [activeCategoryId, setActiveCategoryId] = useState("security");
  const [openIndex, setOpenIndex] = useState(0);
  const navInnerRef = useRef(null);
  const navItemRefs = useRef([]);
  const [arrowOffset, setArrowOffset] = useState(0);

  const activeCategoryIndex = FAQ_NAV_ITEMS.findIndex(
    (category) => category.id === activeCategoryId
  );

  useLayoutEffect(() => {
    const updateArrowOffset = () => {
      const activeItem = navItemRefs.current[activeCategoryIndex];
      const navInner = navInnerRef.current;
      if (!activeItem || !navInner) return;

      const arrowHeight = 16;
      const nextOffset =
        activeItem.offsetTop + activeItem.offsetHeight / 2 - arrowHeight / 2;
      setArrowOffset(nextOffset);
    };

    updateArrowOffset();
    window.addEventListener("resize", updateArrowOffset);
    return () => window.removeEventListener("resize", updateArrowOffset);
  }, [activeCategoryIndex]);

  const handleCategoryChange = (categoryId) => {
    setActiveCategoryId(categoryId);
    setOpenIndex(0);
  };

  return (
    <section className="hw-pricing-faq">
      <h2>FAQ</h2>
      <div className="hw-pricing-faq__layout">
        <nav className="hw-pricing-faq__nav" aria-label="FAQ categories">
          <div className="hw-pricing-faq__nav-inner" ref={navInnerRef}>
            <span
              className="hw-pricing-faq__nav-arrow"
              aria-hidden="true"
              style={{ transform: `translateY(${arrowOffset}px)` }}
            >
              →
            </span>
            <div className="hw-pricing-faq__nav-items">
              {FAQ_NAV_ITEMS.map((category, index) => {
                const isActive = category.id === activeCategoryId;
                return (
                  <button
                    key={category.id}
                    ref={(element) => {
                      navItemRefs.current[index] = element;
                    }}
                    type="button"
                    className={`hw-pricing-faq__nav-item${isActive ? " is-active" : ""}`}
                    onClick={() => handleCategoryChange(category.id)}
                    aria-current={isActive ? "true" : undefined}
                  >
                    {category.label}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>

        <div className="hw-pricing-faq__list">
          {PRICING_FAQ_ITEMS.map((item, index) => {
            const isOpen = openIndex === index;

            return (
              <div
                key={item.question}
                className={`hw-pricing-faq__item${isOpen ? " is-open" : ""}`}
              >
                <button
                  type="button"
                  className="hw-pricing-faq__q"
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  aria-expanded={isOpen}
                >
                  <span>{item.question}</span>
                  <em aria-hidden="true">{isOpen ? "−" : "+"}</em>
                </button>
                {isOpen && <p className="hw-pricing-faq__a">{item.answer}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HomeFaqAndCta({ faqs = HOME_FAQS, ctaSecondaryLink = "/ProductIntelligence" }) {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="hw-home-faq-cta">
      <section className="hw-home-faq">
        <h2>FAQ</h2>
        <div className="hw-home-faq__list">
          {faqs.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={item.question} className={`hw-home-faq__item ${isOpen ? "is-open" : ""}`}>
                <button
                  type="button"
                  className="hw-home-faq__q"
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  aria-expanded={isOpen}
                >
                  <span>{item.question}</span>
                  <em aria-hidden="true">{isOpen ? "−" : "+"}</em>
                </button>
                {isOpen && <p className="hw-home-faq__a">{item.answer}</p>}
              </div>
            );
          })}
        </div>
      </section>

      <HomeCta ctaSecondaryLink={ctaSecondaryLink} />
    </div>
  );
}

export default function FAQSection({
  variant = "pricing",
  faqs,
  ctaSecondaryLink,
  showCta = false,
}) {
  if (variant === "home") {
    return <HomeFaqAndCta faqs={faqs} ctaSecondaryLink={ctaSecondaryLink} />;
  }

  return (
    <>
      <PricingFaqSection />
      {showCta && <HomeCta ctaSecondaryLink={ctaSecondaryLink} />}
    </>
  );
}
